'use client';

import { useEffect, ReactNode } from 'react';
import { sendAppierEvent } from '@/utils/appier';

export function AppierServiceWorker(): null {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/qg-sw.9d4db29fccff1241c86e.js').then(
        function (registration) {
          console.log('Service Worker registration successful with scope: ', registration.scope);
        },
        function (err) {
          console.log('Service Worker registration failed: ', err);
        },
      );
    }
  }, []);

  return null;
}

// 瀏覽產品頁
export function AppierProductViewed(): null {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.includes('callback')) return;

    const _appier_product_viewed_date = window.localStorage.getItem('_appier_product_viewed_date');

    if (_appier_product_viewed_date) {
      const _appier_product_viewed_date_time = parseInt(_appier_product_viewed_date);
      const _appier_product_viewed_date_now = new Date().getTime();

      if (_appier_product_viewed_date_now - _appier_product_viewed_date_time < 3600000) {
        return;
      }
    }

    sendAppierEvent('service_viewed', {
      service_name: '機場接送',
      service_id: 'UGO_TW',
      service_url: window.location.pathname,
    });

    // 設定localStorage _appier_product_viewed_date
    // 一小時內 | 用戶持續瀏覽 => 都不會再發送一次
    window.localStorage.setItem('_appier_product_viewed_date', new Date().getTime().toString());
  }, []);

  return null;
}

interface AppierBannerClickedProps {
  banner_id: string;
  banner_name: string;
  redirect_url: string;
  children: ReactNode;
}

// 用戶點擊 Banner
export function AppierBannerClicked({
  banner_id,
  banner_name,
  redirect_url,
  children,
}: AppierBannerClickedProps) {
  const handleClick = (): void => {
    sendAppierEvent('banner_clicked', {
      banner_id,
      banner_name,
      redirect_url,
    });
  };

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}

interface AppierOrderSuccessProps {
  ugo_order_id: string;
  user_id: string;
  phoneNo: string;
  user_name: string;
  email: string;
  flight_direction: string;
  total_amount: number;
  location: string;
  airport_location_name: string;
  paymentStatus: string;
  departure_iata_code: string;
  arrival_iata_code: string;
  pickup_at_date: string;
}

// 完成下單
export function AppierOrderSuccess({
  ugo_order_id,
  user_id,
  phoneNo,
  user_name,
  email,
  flight_direction,
  total_amount,
  location,
  airport_location_name,
  paymentStatus,
  departure_iata_code,
  arrival_iata_code,
  pickup_at_date,
}: AppierOrderSuccessProps): null {
  useEffect(() => {
    sendAppierEvent('order_completed', {
      order_id: ugo_order_id,
      user_id,
      phoneNo,
      user_name,
      email,
      flight_direction,
      total_amount,
      location,
      airport_location_name,
      url: window.location.pathname,
      service_name: '機場接送',
      service_id: 'UGO_TW',
      payment_status: paymentStatus,
      departure_iata_code: departure_iata_code,
      arrival_iata_code: arrival_iata_code,
      pickup_at_date,
    });
  }, []);
  return null;
}

interface AppierOrderFailedProps {
  ugo_order_id: string;
  user_id: string;
  phoneNo: string;
  user_name: string;
  email: string;
  flight_direction: string;
  total_amount: number;
  location: string;
  airport_location_name: string;
}

export function AppierOrderFailed({
  ugo_order_id,
  user_id,
  phoneNo,
  user_name,
  email,
  flight_direction,
  total_amount,
  location,
  airport_location_name,
}: AppierOrderFailedProps): null {
  useEffect(() => {
    sendAppierEvent('payment_failed', {
      ugo_order_id,
      user_id,
      phoneNo,
      user_name,
      email,
      flight_direction,
      total_amount,
      location,
      airport_location_name,
      url: window.location.pathname,
    });
  }, []);
  return null;
}

interface AppierUserActionProps {
  user_id: string;
  phoneNo: string;
  user_name: string;
  children: ReactNode;
}

// 加入行事曆
export function AppierAddCalendar({
  user_id,
  phoneNo,
  user_name,
  children,
}: AppierUserActionProps) {
  const handleClick = (): void => {
    sendAppierEvent('add_calendar', {
      user_id,
      phoneNo,
      user_name,
    });
  };
  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}

interface AppierAddArrivalProps extends AppierUserActionProps {
  disabled?: boolean;
}

// 預約回程 或 預約失敗時的再預約
export function AppierAddArrival({
  user_id,
  phoneNo,
  user_name,
  children,
  disabled,
}: AppierAddArrivalProps) {
  const handleClick = (): void => {
    if (disabled) {
      sendAppierEvent('resubmit', {
        user_id,
        phoneNo,
        user_name,
      });
      return;
    }
    sendAppierEvent('add_arrival', {
      user_id,
      phoneNo,
      user_name,
    });
  };
  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}

interface AppierPostClickProps {
  postId: string;
  postName: string;
  children: ReactNode;
}

export function AppierPostClick({ postId, postName, children }: AppierPostClickProps) {
  const handleClick = (): void => {
    sendAppierEvent('post_clicked', {
      post_id: postId,
      post_name: postName,
    });
  };
  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}

interface AppierPostViewedProps {
  postId: string;
  postName: string;
}

export function AppierPostViewed({ postId, postName }: AppierPostViewedProps): null {
  useEffect(() => {
    sendAppierEvent('post_viewed', {
      post_id: postId,
      post_name: postName,
    });
  }, []);
  return null;
}
